import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { fetchPublicEmailSettingsApi, updatePublicEmailSettingsApi } from "../../http/userAPI";
import {
    EMAIL_NOTIFICATION_GROUPS,
    EMAIL_NOTIFICATION_KEYS,
    mergeEmailNotificationSettings,
} from "../../helpers/emailNotificationSettings";

const buildBulkSettings = (value) => EMAIL_NOTIFICATION_KEYS.reduce((acc, key) => {
    acc[key] = value;
    return acc;
}, {});

const EmailUnsubscribe = () => {
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const uid = params.get("uid") || "";
    const token = params.get("token") || "";
    const [settings, setSettings] = useState(mergeEmailNotificationSettings());
    const [username, setUsername] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const data = await fetchPublicEmailSettingsApi({ uid, token });
                if (!mounted) return;
                setUsername(data.username || "");
                setSettings(mergeEmailNotificationSettings(data.settings));
            } catch (e) {
                toast.error(e?.response?.data?.message || "This unsubscribe link is invalid.");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [uid, token]);

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setSettings((prev) => ({ ...prev, [name]: checked }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const data = await updatePublicEmailSettingsApi({
                uid,
                token,
                settings: mergeEmailNotificationSettings(settings),
            });
            setSettings(mergeEmailNotificationSettings(data.settings));
            toast.success("Email preferences saved.");
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to save email preferences.");
        } finally {
            setSaving(false);
        }
    };

    const disabled = loading || saving;
    const allSelected = EMAIL_NOTIFICATION_KEYS.every((key) => settings[key] === true);
    const allDeselected = EMAIL_NOTIFICATION_KEYS.every((key) => settings[key] === false);

    return (
        <div className="min-vh-100 bg-white d-flex align-items-start justify-content-center py-5 px-3">
            <Card className="border border-secondary shadow-sm" style={{ maxWidth: 720, width: "100%" }}>
                <Card.Body className="p-4">
                    <div className="text-center mb-4">
                        <img
                            src="/Virtalibry_logo_sub_no_bg.png"
                            alt="Virtalibry"
                            style={{ maxWidth: 220, width: "70%" }}
                        />
                    </div>

                    <h4 className="text-success mb-2">Email Preferences</h4>
                    <p className="text-muted mb-4">
                        {username ? `Hi, ${username}. ` : ""}
                        Choose which Virtalibry emails you want to receive.
                    </p>

                    {loading ? (
                        <div className="d-flex align-items-center gap-2 text-muted">
                            <Spinner size="sm" /> Loading preferences...
                        </div>
                    ) : (
                        <>
                            <div className="d-flex flex-wrap gap-3 mb-4">
                                <Form.Check
                                    className="custom-checkbox d-flex align-items-center"
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={() => setSettings(buildBulkSettings(true))}
                                    disabled={disabled}
                                    label={<span className="ms-2 small">Select all</span>}
                                />
                                <Form.Check
                                    className="custom-checkbox d-flex align-items-center"
                                    type="checkbox"
                                    checked={allDeselected}
                                    onChange={() => setSettings(buildBulkSettings(false))}
                                    disabled={disabled}
                                    label={<span className="ms-2 small">Deselect all</span>}
                                />
                            </div>

                            {EMAIL_NOTIFICATION_GROUPS.map((group) => (
                                <section key={group.id} className="mb-4">
                                    <h6 className="text-muted mb-1">{group.title}</h6>
                                    <p className="text-muted small mb-3">{group.description}</p>

                                    {group.items.map((item) => (
                                        <Form.Check
                                            key={item.key}
                                            className="custom-checkbox mb-3 d-flex align-items-start"
                                            type="checkbox"
                                            name={item.key}
                                            checked={settings[item.key] === true}
                                            onChange={handleCheckboxChange}
                                            disabled={disabled}
                                            label={
                                                <span className="ms-2">
                                                    <strong>{item.title}</strong>
                                                    <span className="d-block text-muted small">{item.description}</span>
                                                </span>
                                            }
                                        />
                                    ))}
                                </section>
                            ))}

                            <div className="d-flex justify-content-end">
                                <Button variant="success" disabled={disabled} onClick={save}>
                                    {saving ? <Spinner size="sm" /> : "Save Preferences"}
                                </Button>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default EmailUnsubscribe;
